import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import React, { useContext, useEffect, useState } from 'react';
import { useGetAllDevicesByLicenseAsync } from '../../../services/Devices/hooks/useMutation';
import { IDeviceSimpleProps } from '../../../services/Devices/types';
import DropSelectComponent, {
  IDropSelectOptions,
} from '../../../components/DropSelectComponent';
import {
  useCreateSipClientLinkedDeviceAsync,
  useUpdateSipClientLinkedDeviceAsync,
} from '../../../services/SipClientLinkedDeviceService/hooks/useMutation';
import zod from 'zod';
import { SipClientLinkedDeviceFormSchema } from '../../../services/SipClientLinkedDeviceService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AppContext } from '../../../context/AppContextProvider';
import ButtonComponent from '../../../components/ButtonComponent';
import { Check, Close } from '@mui/icons-material';
import { Grid2 } from '@mui/material';
import { ISipClientLinkedDeviceProps } from '../../../services/SipClientLinkedDeviceService/types';

interface DeviceLinkWithSipClientEditionModalProps {
  isVisible: boolean;
  onClose: () => void;
  sipClientSelected?: ISipClientLinkedDeviceProps;
}

type SipClientLinkedDeviceFormInput = zod.infer<
  typeof SipClientLinkedDeviceFormSchema
>;

const DeviceLinkWithSipClientEditionModal = ({
  isVisible,
  onClose,
  sipClientSelected,
}: DeviceLinkWithSipClientEditionModalProps) => {
  const { control, reset, handleSubmit } =
    useForm<SipClientLinkedDeviceFormInput>({
      resolver: zodResolver(SipClientLinkedDeviceFormSchema),
      defaultValues: { name: '', deviceId: 0 },
    });
  const { showSnackbar } = useContext(AppContext);
  const [devices, setDevices] = useState<IDeviceSimpleProps[]>([]);
  const { mutateAsync: mutateGetAllDevicesByLicenseAsync } =
    useGetAllDevicesByLicenseAsync();

  const getDevicesSelectionOptions = (): IDropSelectOptions[] => {
    return devices.map((dev) => {
      return {
        option: dev.name,
        value: dev.id,
      } as IDropSelectOptions;
    });
  };

  const {
    mutateAsync: mutateCreateSipClientLinkedDeviceAsync,
    isLoading: isLoadingCreateSipClientLinkedDeviceAsync,
  } = useCreateSipClientLinkedDeviceAsync();

  const {
    mutateAsync: mutateUpdateSipClientLinkedDeviceAsync,
    isLoading: isLoadingUpdateSipClientLinkedDeviceAsync,
  } = useUpdateSipClientLinkedDeviceAsync();

  const onSaveOrUpdateSipClientLinkedDevice = async (
    data: SipClientLinkedDeviceFormInput,
  ) => {
    if (!data.id) {
      await mutateCreateSipClientLinkedDeviceAsync(data, {
        onSuccess: (_) => {
          showSnackbar('Ramal vínculado com sucesso.', 'success');
        },
      });
    } else {
      await mutateUpdateSipClientLinkedDeviceAsync(
        { id: data.id, data },
        {
          onSuccess: (_) => {
            showSnackbar('Vínculo atualizado com sucesso.', 'success');
          },
        },
      );
    }
    reset();
    onClose();
  };

  useEffect(() => {
    mutateGetAllDevicesByLicenseAsync({}).then(setDevices);
  }, []);

  useEffect(() => {
    if (sipClientSelected) {
      const item = {
        ...sipClientSelected,
        deviceId: sipClientSelected.device.id,
      };
      reset(item, { keepDefaultValues: true });
    } else reset();
  }, [sipClientSelected]);
  return (
    <ModalComponent
      isOpen={isVisible}
      onClose={onClose}
      title="Vínculo de ramal com dispositivo"
    >
      <Controller
        render={({ field: { value, onChange } }) => (
          <TextFieldComponent
            label="Ramal"
            onChangeText={onChange}
            value={value}
          />
        )}
        name={'name'}
        control={control}
      />
      <Controller
        render={({ field: { value, onChange } }) => (
          <DropSelectComponent
            label="Dispositivo"
            valueSelected={value}
            options={getDevicesSelectionOptions()}
            onChange={onChange}
          />
        )}
        name={'deviceId'}
        control={control}
      />
      <Grid2 columnSpacing={2} marginTop={3} container>
        <ButtonComponent
          startIcon={<Check />}
          type={'submit'}
          loading={
            isLoadingUpdateSipClientLinkedDeviceAsync ||
            isLoadingCreateSipClientLinkedDeviceAsync
          }
          onClick={handleSubmit(onSaveOrUpdateSipClientLinkedDevice)}
        >
          Salvar
        </ButtonComponent>
        <ButtonComponent
          startIcon={<Close />}
          color={'error'}
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancelar
        </ButtonComponent>
      </Grid2>
    </ModalComponent>
  );
};

export default DeviceLinkWithSipClientEditionModal;
